/**
 * ManualEventModal
 *
 * Modal de criação/edição de eventos manuais do Calendário Global.
 * Faz validação com Zod e usa `useManualCalendarEvent` para persistência.
 *
 * - Modo "create": apenas calendários com permissão edit/manage podem receber eventos.
 * - Modo "edit": o evento já vem com calendar_id fixo (não permite trocar de calendário).
 * - Modo "delete": botão "Excluir" disponível em modo edição.
 *
 * RELEASE:
 *   2026-08-04 v2.1.0 - Descrição em rich text (HTML sanitizado) igual ao módulo de Tarefas.
 *   2026-06-25 v1.1.0 - Layout padronizado (header premium + footer).
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 3 Calendário Global)
 */

import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { processHtmlForSave, stripHtml } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useManualCalendarEvent } from '@/hooks/useManualCalendarEvent';
import type { UserCalendar } from '@/hooks/useCalendars';
import type { UnifiedCalendarEvent } from '@/hooks/useCalendarUnifiedEvents';
import { CalendarPlus, CalendarClock, Loader2, Trash2, X, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';




// ----- Recorrência (presets → RRULE) ----------------------------------------
type RecurrencePreset = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

const RECURRENCE_OPTIONS: Array<{ value: RecurrencePreset; label: string }> = [
  { value: 'none', label: 'Não se repete' },
  { value: 'daily', label: 'Todos os dias' },
  { value: 'weekly', label: 'Toda semana' },
  { value: 'monthly', label: 'Todo mês' },
  { value: 'yearly', label: 'Todo ano' },
];

function presetToRrule(p: RecurrencePreset): string | null {
  switch (p) {
    case 'daily':
      return 'FREQ=DAILY';
    case 'weekly':
      return 'FREQ=WEEKLY';
    case 'monthly':
      return 'FREQ=MONTHLY';
    case 'yearly':
      return 'FREQ=YEARLY';
    default:
      return null;
  }
}

function rruleToPreset(rule: string | null | undefined): RecurrencePreset {
  if (!rule) return 'none';
  const upper = rule.toUpperCase();
  if (upper.includes('FREQ=DAILY')) return 'daily';
  if (upper.includes('FREQ=WEEKLY')) return 'weekly';
  if (upper.includes('FREQ=MONTHLY')) return 'monthly';
  if (upper.includes('FREQ=YEARLY')) return 'yearly';
  return 'none';
}

// ----- Lembretes (presets em minutos antes do início) -----------------------
const REMINDER_PRESETS: Array<{ minutes: number; label: string }> = [
  { minutes: 5, label: '5 min' },
  { minutes: 10, label: '10 min' },
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 h' },
  { minutes: 120, label: '2 h' },
  { minutes: 1440, label: '1 dia' },
];

// Schema de validação base
const baseSchema = z.object({
  calendarId: z.string().uuid({ message: 'Selecione um calendário' }),
  title: z.string().trim().min(1, 'Título obrigatório').max(255),
  description: z.string().max(5000).optional().nullable(),
  location: z.string().max(500).optional().nullable(),
  startAt: z.string().min(1, 'Início obrigatório'),
  endAt: z.string().min(1, 'Fim obrigatório'),
  allDay: z.boolean(),
});

export interface ManualEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calendars: UserCalendar[];
  /** Se informado: modo edição. */
  event?: UnifiedCalendarEvent | null;
  /** Calendário pré-selecionado em modo criação. */
  defaultCalendarId?: string | null;
  /** v2.0.0: início pré-preenchido (ISO) ao criar a partir de um clique no grid. */
  defaultStartAt?: string | null;
  /** v2.0.0: fim pré-preenchido (ISO). */
  defaultEndAt?: string | null;
  /** v2.0.0: define "dia inteiro" no prefill (clique no grid de mês). */
  defaultAllDay?: boolean;
  /** v2.0.0: abre já com sincronização Google ligada (?new=google). */
  defaultGoogleSync?: boolean;
}


/** Converte ISO -> 'YYYY-MM-DDTHH:mm' (datetime-local). */
function toLocalInput(iso: string | null | undefined, allDay = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (allDay) return d.toISOString().slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Converte input datetime-local para ISO. */
function fromLocalInput(value: string, allDay = false): string {
  if (!value) return '';
  if (allDay) return new Date(`${value}T00:00:00`).toISOString();
  return new Date(value).toISOString();
}

export function ManualEventModal({
  open,
  onOpenChange,
  calendars,
  event,
  defaultCalendarId,
  defaultStartAt,
  defaultEndAt,
  defaultAllDay,
  defaultGoogleSync,
}: ManualEventModalProps) {
  const isEdit = !!event;
  const { createEventAsync, updateEventAsync, deleteEventAsync, isCreating, isUpdating, isDeleting } =
    useManualCalendarEvent();

  // Calendários elegíveis para receber novos eventos
  const editableCalendars = useMemo(
    () => calendars.filter((c) => c.user_permission === 'edit' || c.user_permission === 'manage'),
    [calendars]
  );

  // Calendários vinculados a uma integração Google (v2.0.0)
  const googleCalendars = useMemo(
    () => editableCalendars.filter((c) => !!c.google_integration_id),
    [editableCalendars]
  );
  const hasGoogleCalendar = googleCalendars.length > 0;

  // Estado do formulário
  const [calendarId, setCalendarId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  // Lembretes em minutos antes do início do evento (multi-seleção via chips)
  const [reminderMinutes, setReminderMinutes] = useState<number[]>([]);
  // Recorrência simplificada (preset → RRULE)
  const [recurrence, setRecurrence] = useState<RecurrencePreset>('none');
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** v2.0.0: quando ligado, restringe a seleção a calendários Google. */
  const [googleSync, setGoogleSync] = useState(false);
  /** v2.0.0: bloco "Mais opções" (colapsável). */
  const [showAdvanced, setShowAdvanced] = useState(false);
  /**
   * v2.2.0: indica que o formulário já foi hidratado com os dados do evento.
   * O editor rich text (TipTap) monta o conteúdo apenas na criação da instância,
   * por isso só é renderizado depois da hidratação — caso contrário a descrição
   * salva não aparecia ao editar.
   */
  const [hydrated, setHydrated] = useState(false);


  // Lista efetiva de calendários no seletor conforme o toggle Google
  const selectableCalendars = googleSync ? googleCalendars : editableCalendars;

  // Ao alternar o toggle Google, garante um calendário válido selecionado
  useEffect(() => {
    if (isEdit) return;
    const valid = selectableCalendars.some((c) => c.id === calendarId);
    if (!valid) setCalendarId(selectableCalendars[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleSync, selectableCalendars.length, isEdit]);


  // Reset ao abrir / trocar evento
  useEffect(() => {
    if (!open) {
      // Ao fechar, desmonta o editor para que a próxima abertura recrie-o já hidratado
      setHydrated(false);
      return;
    }

    if (event) {
      setCalendarId(event.calendar_id);
      setTitle(event.title);
      // v: prioriza o HTML (rich text); cai para o texto puro em eventos antigos
      setDescription(event.description_html ?? event.description ?? '');
      setLocation(event.location ?? '');
      setAllDay(event.all_day);
      setStartAt(toLocalInput(event.start_at, event.all_day));
      setEndAt(toLocalInput(event.end_at, event.all_day));
      // Hidrata lembretes a partir do JSONB do evento
      const evReminders = Array.isArray(event.reminders) ? event.reminders : [];
      setReminderMinutes(
        evReminders
          .map((r) => Number((r as { minutes?: number })?.minutes))
          .filter((n) => Number.isFinite(n) && n > 0),
      );
      // Hidrata recorrência a partir do RRULE armazenado
      setRecurrence(rruleToPreset(event.recurrence_rule ?? null));
      setGoogleSync(false);
      // Em edição, já mostra tudo (usuário costuma revisar detalhes)
      setShowAdvanced(true);
    } else {
      // v2.0.0: toggle Google só liga se houver calendário Google conectado
      const wantsGoogle = !!defaultGoogleSync && googleCalendars.length > 0;
      setGoogleSync(wantsGoogle);
      const pool = wantsGoogle ? googleCalendars : editableCalendars;
      setCalendarId(defaultCalendarId ?? pool[0]?.id ?? '');
      setTitle('');
      setDescription('');
      setLocation('');
      const isAllDay = !!defaultAllDay;
      setAllDay(isAllDay);
      // Prefill: usa o slot clicado no grid quando informado; senão "agora"
      const start = defaultStartAt ? new Date(defaultStartAt) : new Date();
      const end = defaultEndAt
        ? new Date(defaultEndAt)
        : new Date(start.getTime() + 60 * 60 * 1000);
      setStartAt(toLocalInput(start.toISOString(), isAllDay));
      setEndAt(toLocalInput(end.toISOString(), isAllDay));
      setReminderMinutes([]);
      setRecurrence('none');
      // Criação sempre começa compacta (estilo Google Agenda)
      setShowAdvanced(false);
    }
    setErrors({});
    // Libera a renderização do editor rich text já com o conteúdo correto
    setHydrated(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event, defaultCalendarId, defaultStartAt, defaultEndAt, defaultAllDay, defaultGoogleSync]);


  const handleSubmit = async () => {
    const parsed = baseSchema.safeParse({
      calendarId,
      title,
      description: stripHtml(description),
      location,
      startAt,
      endAt,
      allDay,
    });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[String(issue.path[0])] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    const startIso = fromLocalInput(startAt, allDay);
    const endIso = fromLocalInput(endAt, allDay);

    if (new Date(endIso) < new Date(startIso)) {
      setErrors({ endAt: 'Fim deve ser maior ou igual ao início' });
      return;
    }

    // Monta payload comum de lembretes/recorrência
    const reminders = reminderMinutes
      .filter((n) => Number.isFinite(n) && n > 0)
      .map((m) => ({ method: 'popup', minutes: m }));
    const recurrenceRule = presetToRrule(recurrence);

    // v2.2.0: sinaliza limpeza explícita (a RPC usa COALESCE; NULL = "não alterar")
    const plainDescription = stripHtml(description).trim();
    const clearDescription = plainDescription.length === 0;
    const clearLocation = location.trim().length === 0;
    const clearRecurrence = recurrenceRule === null;

    try {
      if (isEdit && event) {
        await updateEventAsync({
          eventId: event.id,
          title,
          description: plainDescription || null,
          descriptionHtml: processHtmlForSave(description).sanitized || null,
          location: location || null,
          startAt: startIso,
          endAt: endIso,
          allDay,
          reminders,
          recurrenceRule,
          clearDescription,
          clearLocation,
          clearRecurrence,
        });

      } else {
        await createEventAsync({
          calendarId,
          title,
          description: stripHtml(description) || null,
          descriptionHtml: processHtmlForSave(description).sanitized || null,
          location: location || null,
          startAt: startIso,
          endAt: endIso,
          allDay,
          reminders,
          recurrenceRule,
        });
      }
      onOpenChange(false);
    } catch {
      // toast já tratado no hook
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    if (!window.confirm('Excluir este evento? Esta ação não pode ser desfeita.')) return;
    try {
      await deleteEventAsync(event.id);
      onOpenChange(false);
    } catch {
      // toast já tratado
    }
  };

  const isBusy = isCreating || isUpdating || isDeleting;
  const canEdit = !isEdit || event?.source_type === 'manual';

  /**
   * v2.2.0 — Participantes do evento (somente leitura).
   * O JSONB pode vir em formatos distintos (Google x manual); normalizamos
   * para { email, displayName } ignorando entradas inválidas.
   */
  const eventAttendees = useMemo(() => {
    const raw = Array.isArray(event?.attendees) ? (event?.attendees as unknown[]) : [];
    return raw
      .map((a) => {
        const item = (a ?? {}) as { email?: string; displayName?: string; name?: string };
        return { email: String(item.email ?? ''), displayName: item.displayName ?? item.name ?? '' };
      })
      .filter((a) => a.email.length > 0);
  }, [event]);


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[90vh] h-[90dvh] sm:h-auto p-0 gap-0 overflow-hidden flex flex-col sm:rounded-lg">
        {/* Header premium padronizado */}
        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent shrink-0">
          <div className="flex items-start gap-3 min-w-0 pr-8">
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
              {isEdit ? (
                <CalendarClock className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              ) : (
                <CalendarPlus className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="truncate">
                {isEdit ? 'Editar evento' : 'Novo evento'}
              </DialogTitle>
              <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                Configure data, horário, recorrência e lembretes do evento.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Corpo rolável */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
        {!canEdit ? (
          <p className="text-sm text-muted-foreground">
            Este evento não é editável por aqui (origem: {event?.source_type}).
          </p>
        ) : (
          <div className="space-y-3">

            {/* Sincronização com Google Agenda (v2.0.0)
                Padrão = evento manual (local). Ao ligar, restringe a lista de
                calendários àqueles vinculados a uma integração Google. */}
            {!isEdit && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <img
                    src={googleCalendarIcon}
                    alt=""
                    aria-hidden
                    className="h-5 w-5 shrink-0 object-contain"
                  />
                  <div className="min-w-0">
                    <Label className="text-sm">Sincronizar com Google Agenda</Label>
                    <p className="text-xs text-muted-foreground">
                      {hasGoogleCalendar
                        ? 'Grava o evento em um calendário Google conectado.'
                        : 'Nenhum calendário Google conectado.'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={googleSync}
                  disabled={!hasGoogleCalendar}
                  onCheckedChange={setGoogleSync}
                />
              </div>
            )}

            {/* Título */}
            <div>
              <Label>Título</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={255}
                placeholder="Ex.: Reunião com cliente"
                autoFocus
              />
              {errors.title && <p className="text-xs text-destructive mt-1">{errors.title}</p>}
            </div>

            {/* Início / Fim */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Início</Label>
                <Input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                />
                {errors.startAt && (
                  <p className="text-xs text-destructive mt-1">{errors.startAt}</p>
                )}
              </div>
              <div>
                <Label>Fim</Label>
                <Input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                />
                {errors.endAt && (
                  <p className="text-xs text-destructive mt-1">{errors.endAt}</p>
                )}
              </div>
            </div>

            {/* Calendário */}
            <div>
              <Label>Calendário</Label>
              <Select value={calendarId} onValueChange={setCalendarId} disabled={isEdit}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um calendário" />
                </SelectTrigger>
                <SelectContent>
                  {selectableCalendars.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-sm"
                          style={{ backgroundColor: c.color }}
                          aria-hidden
                        />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.calendarId && (
                <p className="text-xs text-destructive mt-1">{errors.calendarId}</p>
              )}
            </div>

            {/* Descrição (rich text) — agora visível no modo simples
                v2.2.0: só monta o editor depois da hidratação (TipTap fixa o
                conteúdo inicial na criação da instância). */}
            <div className="space-y-1.5">
              <Label>Descrição</Label>
              {hydrated ? (
                <RichTextEditor
                  key={event?.id ?? 'new-event'}
                  content={description}
                  onChange={setDescription}
                  placeholder="Detalhes do evento (aceita formatação, links e listas)"
                  minHeight="100px"
                  maxHeight="220px"
                />
              ) : (
                <div className="h-[100px] rounded-md border border-border/60 bg-muted/30" />
              )}
            </div>

            {/* v2.2.0: em edição, informa se o evento está sincronizado no Google */}
            {isEdit && event?.google_event_id && (
              <div className="flex items-center gap-2.5 rounded-md border border-border/60 px-3 py-2">
                <img
                  src={googleCalendarIcon}
                  alt=""
                  aria-hidden
                  className="h-4 w-4 shrink-0 object-contain"
                />
                <p className="text-xs text-muted-foreground">
                  Sincronizado com o Google Agenda — as alterações são enviadas automaticamente.
                </p>
              </div>
            )}

            {/* v2.2.0: participantes existentes (somente leitura) */}
            {isEdit && eventAttendees.length > 0 && (
              <div className="space-y-1.5">
                <Label>Participantes</Label>
                <div className="flex flex-wrap gap-1.5">
                  {eventAttendees.map((a) => (
                    <span
                      key={a.email}
                      className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs text-muted-foreground"
                    >
                      {a.displayName || a.email}
                    </span>
                  ))}
                </div>
              </div>
            )}




            {/* ----- Opções avançadas (colapsável, estilo Google Agenda) ----- */}
            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ChevronDown
                    className={cn(
                      'h-3.5 w-3.5 transition-transform',
                      showAdvanced && 'rotate-180',
                    )}
                  />
                  {showAdvanced ? 'Menos opções' : 'Mais opções'}
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent className="space-y-3 pt-2">
                {/* Dia inteiro */}
                <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                  <div>
                    <Label className="text-sm">Dia inteiro</Label>
                    <p className="text-xs text-muted-foreground">Sem horário específico</p>
                  </div>
                  <Switch
                    checked={allDay}
                    onCheckedChange={(v) => {
                      setAllDay(v);
                      if (v) {
                        setStartAt(startAt.slice(0, 10));
                        setEndAt(endAt.slice(0, 10));
                      } else {
                        setStartAt(`${startAt.slice(0, 10)}T09:00`);
                        setEndAt(`${endAt.slice(0, 10)}T10:00`);
                      }
                    }}
                  />
                </div>

                {/* Local */}
                <div>
                  <Label>Local</Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    maxLength={500}
                    placeholder="Endereço, link ou sala"
                  />
                </div>

                {/* Recorrência (Fase 6) */}
                <div>
                  <Label>Repetição</Label>
                  <Select value={recurrence} onValueChange={(v) => setRecurrence(v as RecurrencePreset)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRENCE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Lembretes (Fase 6) */}
                <div>
                  <Label>Lembretes</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {REMINDER_PRESETS.map((preset) => {
                      const active = reminderMinutes.includes(preset.minutes);
                      return (
                        <Badge
                          key={preset.minutes}
                          variant={active ? 'default' : 'outline'}
                          className="cursor-pointer select-none gap-1"
                          onClick={() =>
                            setReminderMinutes((prev) =>
                              prev.includes(preset.minutes)
                                ? prev.filter((m) => m !== preset.minutes)
                                : [...prev, preset.minutes].sort((a, b) => a - b),
                            )
                          }
                        >
                          {preset.label}
                          {active && <X className="h-3 w-3" />}
                        </Badge>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tempo antes do início do evento. Toque para adicionar/remover.
                  </p>
                </div>




              </CollapsibleContent>
            </Collapsible>

          </div>
        )}
        </div>

        {/* Footer padronizado */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10 shrink-0">
          {isEdit && canEdit && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isBusy}
              className="mr-auto gap-2"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Excluir
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
            Cancelar
          </Button>
          {canEdit && (
            <Button onClick={handleSubmit} disabled={isBusy}>
              {(isCreating || isUpdating) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Salvar' : 'Criar'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>

  );
}
